import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import type { NeedsYou } from './useNeedsYou'

/**
 * The kinds of record a screen can hand to the Ask dock. Each one is something
 * the merchant can have open in front of them and ask about — "why did you send
 * this?", "what do you know about her?" — so the dock can pin the conversation
 * to the record rather than making them describe it.
 */
export type AskEntityType = 'decision' | 'customer' | 'product' | 'authority_exception' | 'attribution_order'

/**
 * The record the merchant currently has open, published by the screen that
 * opened it. The screen owns this: it knows what is on screen, and the shell
 * does not.
 */
export interface AskEntity {
  type: AskEntityType
  id: string
  /** Human-readable record name, for the dock's pin banner. */
  label: string
  /**
   * The workspace this entity belongs to. Stored rather than inferred, because
   * it is what lets the read below tell "still the same record" from "a record
   * from the workspace we just left" — see `useAskEntityState`.
   */
  workspaceId: string
}

/** The ask-entity half of the shell value. Produced by `useAskEntityState`. */
export interface AskEntityApi {
  askEntity: AskEntity | null
  setAskEntity: (e: AskEntity | null) => void
  clearAskEntity: () => void
  /**
   * Release the pin on one specific record — what a drawer calls when it
   * closes. Matched against the *stored* entity, not the scoped one; see
   * `useAskEntityState`.
   */
  releaseAskEntity: (type: AskEntityType, id: string | null | undefined) => void
}

/** The open-drawer half of the shell value. Produced by `useDrawerRegistry`. */
export interface DrawerRegistryApi {
  /** How many drawers are open right now, across every screen. */
  openDrawerCount: number
  /**
   * Declare one open drawer. Returns the release for *that* registration —
   * idempotent, and safe to call from an unmount cleanup.
   */
  registerDrawer: () => () => void
}

/**
 * The three things the shell lends a screen: the right-hand context panel, the
 * record the merchant currently has open, and whether anything is open over the
 * canvas. All three are published by the screen and consumed by the shell — a
 * screen knows what it is showing; the shell does not.
 */
interface ShellContextValue extends AskEntityApi, DrawerRegistryApi {
  setContextPanel: (node: ReactNode) => void
  /**
   * Open the Ask dock and put the cursor in its composer. The shell owns the
   * dock, so a screen asks for it rather than reaching into it.
   *
   * `draft`, when given, is put IN the composer — never sent. Knowledge's "Or
   * ask her" chips use it (MAR-325). Sending stays the merchant's act, so the
   * per-turn context hint is still built by the dock at the moment they press
   * send, exactly as for anything they type.
   */
  openAsk: (draft?: string) => void
}

export const ShellContext = createContext<ShellContextValue | null>(null)

/**
 * The shell's one "waiting on you" read, lent to screens (MAR-309) — what Ledger's waiting and
 * Shadow banners render from, so the banner, the bell and the Inbox drawer are one fetch and one
 * number rather than three.
 *
 * ⚠️ Its OWN context, not two more fields on `ShellContextValue`, for one reason: this value
 * changes on every refresh tick and every navigation, and the shell value is read by every
 * screen (the context panel, the Ask pin, the drawer registry). Folded in, each queue refresh
 * would re-render every one of them; here it re-renders only the screens that show the queue.
 */
export interface NeedsYouApi {
  needsYou: NeedsYou
  /** Open the shell's Inbox drawer — the same door the bell and `?inbox=open` use. */
  openInbox: () => void
}

export const NeedsYouContext = createContext<NeedsYouApi | null>(null)

/**
 * Read the shell's "waiting on you" queue, from a screen. `null` outside a provider — a screen
 * rendered in isolation has no shell queue, and must then render no banner at all rather than
 * an all-clear it has no basis for.
 *
 * The scoping (workspace, mode) is already done inside `useNeedsYou`, at the read; this is a
 * plain pass-through and must stay one.
 */
export function useNeedsYouState(): NeedsYouApi | null {
  return useContext(NeedsYouContext)
}

/**
 * Lets a screen render content into the shell's right-hand context panel
 * (the After Hours `.mv-ctx`). A page calls `useContextPanel(<node/>, [deps])`;
 * the shell renders it in the aside and switches the grid to `with-ctx`.
 * Mirrors the per-page `<template id="tpl-ctx">` slot in the design's shell.js.
 */
export function useContextPanel(node: ReactNode, deps: React.DependencyList) {
  const ctx = useContext(ShellContext)
  useEffect(() => {
    ctx?.setContextPanel(node)
    return () => ctx?.setContextPanel(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}

/**
 * Owns the ask-entity state for the shell provider.
 *
 * The scoping lives HERE, in the provider, rather than in `useAskEntity`: the
 * value every consumer reads is already scoped, so there is one place it can be
 * got wrong instead of one per consumer. `useAskEntity` is a plain read.
 *
 * A screen publishes with `setAskEntity` when it opens a record and calls
 * `releaseAskEntity` when it closes it — explicit calls, not an effect, so the
 * publish happens in the same act as the open.
 */
export function useAskEntityState(liveWorkspaceId: string | undefined): AskEntityApi {
  const [stored, setStored] = useState<AskEntity | null>(null)

  const setAskEntity = useCallback((e: AskEntity | null) => setStored(e), [])
  const clearAskEntity = useCallback(() => setStored(null), [])

  /**
   * Release one record's pin: what a drawer calls on close, so that closing one
   * drawer never blanks another's pin (Knowledge keeps a customer and a product
   * drawer open at once, and switching tabs closes neither).
   *
   * ⚠️ It matches against `stored`, **not** against the scoped `askEntity`
   * below, and that asymmetry is the point. Under a different workspace the
   * scoped read is `null`, so a release matched against it would match nothing
   * and the stored record would survive a close — switch back and the dock
   * names a record with no drawer open. The consumer's view is deliberately the
   * masked one; the release has to see what is actually held. Do not "fix" this
   * to read the scoped pin for consistency.
   */
  const releaseAskEntity = useCallback(
    (type: AskEntityType, id: string | null | undefined) =>
      setStored((s) => (id && s?.type === type && s.id === id ? null : s)),
    []
  )

  // Validated at render, not in an effect. Clearing in an effect still permits
  // one render of the previous workspace's record, which is one render too
  // many: the dock would head itself "READING THIS CUSTOMER — Sofia Marchetti"
  // from workspace A while workspace B is selected, and the next turn would
  // post B's workspace id alongside A's entity id. A workspace switch has to
  // blank the entity on the first render after the switch, before any effect
  // has run — the drawer that published it may not even be mounted any more to
  // clear it.
  const askEntity = stored?.workspaceId === liveWorkspaceId ? stored : null

  // Note what this does NOT do: the mismatched value is masked, not erased, so
  // A → B → A resurfaces A's record. What makes that survivable is not that the
  // drawer happens to come back (it does today, MAR-205, but that is a bug and
  // it does not come back if the merchant closed the drawer while B was
  // selected) — it is that a release reaches the stored value whatever
  // workspace is on screen, so a close under B still erases it. When the
  // drawers learn to reset on a workspace switch, this has to erase too.
  return useMemo(
    () => ({ askEntity, setAskEntity, clearAskEntity, releaseAskEntity }),
    [askEntity, setAskEntity, clearAskEntity, releaseAskEntity]
  )
}

/**
 * Owns the open-drawer count for the shell provider.
 *
 * **Why a count and not a boolean:** more than one drawer can be open at once —
 * Knowledge keeps a customer and a product drawer, and switching tabs closes
 * neither. A boolean owned by the shell would be set false by whichever one
 * closed first while the other was still over the canvas.
 *
 * **Why it is registered and not derived from the ask-entity pin:** the pin and
 * an open drawer are different facts. The pin survives a workspace switch as a
 * masked value, it is published before the drawer's detail fetch lands, and it
 * is released by a *conditional* match rather than by the drawer's own
 * lifecycle — so reading it as "a drawer is open" would collapse the dock on a
 * screen with nothing open at all. (It used to be worse: the Ask hand-off kept
 * the pin after `Memory` unmounted. That exemption is gone — `useOpenAsk` opens
 * the dock in place — but the two facts are still not the same fact.)
 *
 * **Why a token set and not an integer:** an integer cannot tell "my release
 * already ran" from "someone else's did", so a cleanup invoked twice — React
 * StrictMode, a `useEffect` whose deps churn, a screen unmounting mid-transition
 * — silently decrements another drawer's registration. The symmetric mistake,
 * an extra register, drifts the other way and pins the dock collapsed forever.
 * A token is minted per registration and deleted by id, so a double release is
 * a no-op and a double register is two distinct tokens with two distinct
 * releases. Both directions become arithmetically impossible rather than
 * merely unlikely.
 */
export function useDrawerRegistry(): DrawerRegistryApi {
  const [open, setOpen] = useState<ReadonlySet<number>>(() => new Set())
  const nextToken = useRef(0)

  const registerDrawer = useCallback(() => {
    const token = nextToken.current++
    setOpen((s) => {
      const next = new Set(s)
      next.add(token)
      return next
    })
    return () =>
      setOpen((s) => {
        if (!s.has(token)) return s // already released — not another drawer's slot
        const next = new Set(s)
        next.delete(token)
        return next
      })
  }, [])

  return useMemo(
    () => ({ openDrawerCount: open.size, registerDrawer }),
    [open, registerDrawer]
  )
}

/**
 * Declare that this component currently has a drawer open, for as long as
 * `isOpen` holds. The ergonomic form of `registerDrawer` — one call per drawer,
 * beside the state that opens it.
 *
 * Symmetric by construction: the registration IS the effect, so React runs the
 * release on every teardown it runs the setup for — a StrictMode double-mount,
 * a dep change, and the unmount of a screen whose drawer was still open.
 */
export function useRegisterDrawer(isOpen: boolean): void {
  const ctx = useContext(ShellContext)
  // Outside a provider there is no dock to collapse, so there is nothing to
  // warn about — unlike a dropped ask-entity *publish*, this is a no-op with
  // no user-visible consequence.
  useDrawerRegistration(ctx?.registerDrawer, isOpen)
}

/**
 * Register a ds `Drawer` for as long as it is ON SCREEN — open, and then through its
 * slide-out — and return the `onExited` to give that Drawer (MAR-381).
 *
 * The dock collapses while a drawer is registered, and the Drawer's clipped stage is inset
 * by `--dockw`. Releasing on `open → false` re-expanded the dock the instant a close began,
 * the stage edge moved with it, and the panel jumped ~335px left before sliding out. Holding
 * the registration until `onExited` keeps the dock still for the whole slide. The Drawer
 * always fires `onExited` (transitionend, a timeout fallback, reduced motion, a scope drop),
 * so the hold cannot strand the dock collapsed.
 */
export function useDrawerPresence(open: boolean): () => void {
  const [exiting, setExiting] = useState(false)
  // React's "adjust state when a prop changes" pattern: a close starts the hold, a reopen ends it.
  const [prevOpen, setPrevOpen] = useState(open)
  if (prevOpen !== open) {
    setPrevOpen(open)
    setExiting(!open)
  }
  useRegisterDrawer(open || exiting)
  return useCallback(() => setExiting(false), [])
}

/**
 * The same registration, against a `registerDrawer` held in hand rather than
 * read from context. `AppShell` needs this: it *provides* the context, so
 * `useContext` inside it would read whatever provider is above the shell —
 * nothing — and the Inbox drawer would never register.
 *
 * One implementation, two entry points, so the two cannot drift apart.
 */
export function useDrawerRegistration(
  registerDrawer: (() => () => void) | undefined,
  isOpen: boolean
): void {
  useEffect(() => {
    if (!isOpen || !registerDrawer) return
    return registerDrawer()
  }, [isOpen, registerDrawer])
}

/**
 * Open the Ask dock, from a screen.
 *
 * This is what a drawer's "Ask" button calls. It used to `navigate('/ask')` —
 * the old full-screen Ask, since deleted (`/ask` is now only a redirect to
 * `/ledger?ask=open`) — which is what created the orphaned-pin problem: leaving Knowledge unmounted
 * the drawer, so the pin had to be exempted from the unmount release to
 * survive the trip, and nothing then released it. With a dock there is nowhere
 * to travel to — the drawer stays open, the pin stays backed by a record that
 * is genuinely on screen, and the ordinary close/unmount releases cover it.
 *
 * Outside a provider this warns, like the ask-entity writes: a button that
 * silently does nothing is a wiring bug with no symptom.
 */
export function useOpenAsk(): (draft?: string) => void {
  const ctx = useContext(ShellContext)
  const openAsk = ctx?.openAsk
  return useMemo(() => openAsk ?? (() => warnNoProvider('openAsk')), [openAsk])
}

function warnNoProvider(call: string) {
  // Unconditional rather than dev-gated: `import.meta.env` may only be read in
  // src/config/env.ts (an ESLint error everywhere else), and this only ever
  // fires on a wiring mistake, never on a working path.
  console.warn(
    `${call} was called outside a ShellContext provider — the Ask dock did not receive it.`
  )
}

/**
 * Read (and publish) the record the merchant currently has open. Returns
 * `entity: null` outside a shell provider, and outside the workspace the entity
 * belongs to — the scoping is done by `useAskEntityState`, above.
 */
export function useAskEntity(): {
  entity: AskEntity | null
  setEntity: (e: AskEntity | null) => void
  clearEntity: () => void
  /**
   * Release this record's pin, if it is the one held. Stable for the lifetime
   * of the provider and safe to call from an unmount cleanup — it is a pure
   * state updater, so it needs no view of the current pin to be correct.
   */
  release: (type: AskEntityType, id: string | null | undefined) => void
} {
  const ctx = useContext(ShellContext)
  const entity = ctx?.askEntity ?? null
  const setEntity = ctx?.setAskEntity
  const clearEntity = ctx?.clearAskEntity
  const release = ctx?.releaseAskEntity
  return useMemo(
    () => ({
      entity,
      // Reading outside a provider is legitimate — a screen rendered in
      // isolation has no dock to publish to. *Writing* outside one is not: the
      // publish is silently dropped, and the symptom is a dock that never pins,
      // with nothing in the log to say why. The read stays tolerant; the write
      // says so out loud.
      setEntity: setEntity ?? (() => warnNoProvider('setEntity')),
      clearEntity: clearEntity ?? (() => warnNoProvider('clearEntity')),
      release: release ?? (() => warnNoProvider('release')),
    }),
    [entity, setEntity, clearEntity, release]
  )
}
